<template>
  <v-container fluid>
    <!-- Map Section -->
    <v-row class="mb-6 border-radius-xl">
      <v-col class="border-radius-xl">
        <div class="bg-gradient-primary border-radius-xl" width="100%">
          <div v-if="!!items && items.length > 0" class="border-radius-xl">
            <div
              style="
                height: 500px;
                width: 100%;
                border-radius: 56px;
                position: relative;
              "
              class="border-radius-xl"
            >
              <l-map
                v-if="showMap"
                :zoom="zoom"
                :center="center"
                :options="mapOptions"
                @update:center="centerUpdate"
                @update:zoom="zoomUpdate"
              >
                <l-tile-layer
                  :url="'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'"
                  :attribution="attribution"
                />
                <l-marker
                  v-for="location in items"
                  :key="location.key"
                  :lat-lng="getLatLng(location)"
                  @click="showLocationCard(location, $event)"
                >
                  <l-popup
                    :options="{ maxWidth: 400, className: 'custom-popup' }"
                  >
                    <div class="location-popup">
                      <div class="popup-header">
                        <h3 class="mb-2 text-primary">{{ location.name }}</h3>
                        <p class="text-caption mb-2">
                          {{ location.city }}, {{ location.state }}
                        </p>
                      </div>

                      <div class="popup-content">
                        <div class="devices-section">
                          <h4 class="mb-2">
                            📡 Devices ({{
                              getLocationDevices(location).length
                            }})
                          </h4>

                          <div
                            v-if="getLocationDevices(location).length === 0"
                            class="no-devices"
                          >
                            <p class="text-caption grey--text">
                              No devices found
                            </p>
                          </div>

                          <div v-else class="devices-list">
                            <div
                              v-for="device in getLocationDevices(
                                location
                              ).slice(0, 5)"
                              :key="device.id"
                              class="device-item"
                              @click="navigateToDevice(device.id)"
                            >
                              <div class="device-info">
                                <span class="device-name">{{
                                  device.name
                                }}</span>
                                <span
                                  v-if="device.alertCount > 0"
                                  class="alert-badge error"
                                >
                                  {{ device.alertCount }} alert{{
                                    device.alertCount > 1 ? "s" : ""
                                  }}
                                </span>
                                <span v-else class="alert-badge success">
                                  OK
                                </span>
                              </div>
                              <span class="device-arrow">→</span>
                            </div>

                            <div
                              v-if="getLocationDevices(location).length > 5"
                              class="more-devices"
                            >
                              <p class="text-caption">
                                +{{
                                  getLocationDevices(location).length - 5
                                }}
                                more devices
                              </p>
                            </div>
                          </div>
                        </div>

                        <hr class="my-3" />

                        <div class="popup-actions">
                          <v-btn
                            color="primary"
                            class="view-all-btn"
                            @click="selectLocation(location)"
                          >
                            👁️ View All Devices
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </l-popup>
                </l-marker>
              </l-map>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Locations List -->
    <v-container v-if="!!items && items.length < 1" fluid>
      <h3 class="font-weight-light">You don't have any locations currently</h3>
      <v-card
        ripple
        to="/add-location"
        color=""
        outlined
        width="200"
        class="mt-5"
      >
        <v-card-title
          ><v-icon size="18" class="mr-4">mdi-warehouse</v-icon>
          Create A Location
        </v-card-title>
      </v-card>
    </v-container>
    <v-container v-if="items.length > 0" fluid>
      <v-container
        fluid
        width="100%"
        v-for="item in items"
        :key="item.key"
        class=""
      >
        <v-card
          width="100%"
          outlined
          @click="selectLocation(item)"
          ripple
        >
          <v-container fluid>
            <v-container fluid
              ><h2>{{ item.name }}</h2></v-container
            >
            <v-card-title> {{ item.city }}, {{ item.state }} </v-card-title>
            <v-card-subtitle>
              <strong>{{ getTotalAlertsForLocation(item) + 5 }}</strong> Out Of
              {{ getLocationDevices(item).length }} Devices with Alerts
            </v-card-subtitle>
          </v-container>
        </v-card>
      </v-container>
    </v-container>

    <!-- Custom Alert Notifications -->
    <div class="custom-alerts-container">
      <transition-group name="alert-transition">
        <div
          v-for="(device, index) in alertDevices.filter((d) => d.visible)"
          :key="device.id"
          class="custom-alert"
          :style="{ bottom: `${index * 70 + 16}px` }"
          @click="navigateToDeviceAlerts(device.id)"
        >
          <div class="custom-alert-content">
            <v-icon color="white" size="18" class="mr-2"
              >mdi-alert-circle</v-icon
            >
            <span class="alert-text">
              <strong>{{ device.location || device.name }}</strong
              >: {{ device.count }} Critical Alert{{
                device.count > 1 ? "s" : ""
              }}
            </span>
            <v-btn
              icon
              small
              class="close-btn ml-2"
              @click.stop="closeSnackbar(device)"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
      </transition-group>
    </div>
  </v-container>
</template>
<script>
import { latLng, Icon } from "leaflet";
import { LMap, LTileLayer, LMarker, LPopup } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import { fetchObjects } from "@/services/firebase-helpers";

// Fix Leaflet's icon paths
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "Locations",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
  },
  data() {
    return {
      selectedLocation: null,
      locations: [
        {
          id: 1,
          name: "Facility 1",
          sensorCount: 400,
          city: "Dallas",
          state: "TX",
          alerts: 1,
        },
        {
          id: 2,
          name: "Facility 2",
          sensorCount: 600,
          city: "Sulpher Springs",
          state: "TX",
          alerts: 0,
        },
        {
          id: 3,
          name: "Facility 3",
          sensorCount: 200,
          city: "Atlanta",
          state: "GA",
          alerts: 7,
        },
        {
          id: 4,
          name: "Facility 4",
          sensorCount: 1000,
          city: "College Station",
          state: "TX",
          alerts: 0,
        },
        {
          id: 5,
          name: "Facility 5",
          sensorCount: 200,
          city: "Atlanta",
          state: "GA",
          alerts: 0,
        },
        {
          id: 6,
          name: "Facility 6",
          sensorCount: 200,
          city: "Atlanta",
          state: "GA",
          alerts: 0,
        },
        {
          id: 7,
          name: "Facility 7",
          sensorCount: 200,
          city: "Atlanta",
          state: "GA",
          alerts: 0,
        },
      ],
      zoom: 4,
      center: latLng(39.8283, -98.5795),
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution:
        '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      currentZoom: 4,
      currentCenter: latLng(39.8283, -98.5795),
      showMap: true,
      mapOptions: {
        zoomSnap: 0.5,
      },
      cityCoordinates: {
        Dallas: [32.7767, -96.797],
        "Sulpher Springs": [33.1384, -95.6011],
        Atlanta: [33.749, -84.388],
        "College Station": [30.628, -96.3344],
      },
      alertDevices: [],
      locationDevices: {},
      deviceAlerts: {},
      deviceCache: {},
      clickedLocation: null,
      clickCardPosition: { x: 0, y: 0 },
    };
  },
  computed: {
    items() {
      if (!this.$store.state.locations) {
        return [];
      }

      if (!Array.isArray(this.$store.state.locations)) {
        return [];
      }

      return [...this.$store.state.locations]
        .reverse()
        .filter((i) => i.id !== 22);
    },
    clickCardStyle() {
      if (!this.clickedLocation) return {};

      const mapContainer = document.querySelector(".leaflet-container");
      if (!mapContainer) return {};

      const mapRect = mapContainer.getBoundingClientRect();

      return {
        position: "absolute",
        top: `${this.clickCardPosition.y - mapRect.top + 10}px`,
        left: `${this.clickCardPosition.x - mapRect.left + 40}px`,
        zIndex: 1000,
        pointerEvents: "auto",
      };
    },
  },
  methods: {
    loadUserLocations() {
      if (this.$store.state.auth && this.$store.state.auth.uid) {
        const uid = this.$store.state.auth.uid;
        this.$store.dispatch("handleUserLocations", uid);
        this.fetchDeviceAlerts();
      } else {
        const localUid = localStorage.getItem("uid");
        if (localUid) {
          this.$store.dispatch("handleUserLocations", localUid);
        }
      }
    },

    selectLocation(location) {
      this.selectedLocation = location;
      this.$store.commit("setLocation", this.selectedLocation);
      this.$router.push("/location/" + this.selectedLocation.key);
    },
    zoomUpdate(zoom) {
      this.currentZoom = zoom;
    },
    centerUpdate(center) {
      this.currentCenter = center;
    },
    getLatLng(location) {
      const coords = this.cityCoordinates[location.city];
      if (coords) {
        return latLng(coords[0], coords[1]);
      }
      return this.center;
    },
    async fetchDeviceAlerts() {
      try {
        const allAlerts = await fetchObjects("alerts");

        if (!allAlerts || allAlerts.length === 0) {
          return;
        }

        const criticalAlerts = allAlerts.filter(
          (alert) =>
            alert.status &&
            alert.status.toLowerCase() === "critical" &&
            alert.read !== true
        );

        if (criticalAlerts.length === 0) {
          return;
        }

        const deviceAlertCount = {};
        const deviceNames = {};
        const deviceLocations = {};

        const devices = await fetchObjects("devices");

        criticalAlerts.forEach((alert) => {
          if (!alert.deviceId) return;

          if (!deviceAlertCount[alert.deviceId]) {
            deviceAlertCount[alert.deviceId] = 0;

            const deviceInfo = devices.find((d) => d.key === alert.deviceId);
            deviceNames[alert.deviceId] =
              (deviceInfo && deviceInfo.name) ||
              `Device ${
                alert.deviceId && alert.deviceId.trim()
                  ? alert.deviceId.split("-").pop()
                  : "Unknown"
              }`;

            if (deviceInfo && deviceInfo.location) {
              deviceLocations[alert.deviceId] = deviceInfo.location;
            }
          }

          deviceAlertCount[alert.deviceId]++;
        });

        this.alertDevices = Object.keys(deviceAlertCount).map((deviceId) => ({
          id: deviceId,
          name: deviceNames[deviceId],
          location: deviceLocations[deviceId],
          count: deviceAlertCount[deviceId],
          visible: true,
        }));
      } catch (error) {
        console.error("Error fetching device alerts:", error);
      }
    },
    navigateToDeviceAlerts(deviceId) {
      if (deviceId) {
        const device = this.alertDevices.find((d) => d.id === deviceId);
        if (device) {
          device.visible = false;
        }

        this.$router.push(`/device/${deviceId}/alerts`);
      }
    },
    closeSnackbar(device) {
      device.visible = false;
    },
    getLocationDevices(location) {
      if (!location.devices || location.devices.length === 0) {
        return [];
      }

      return location.devices
        .filter((deviceId) => deviceId)
        .map((deviceId) => {
          const alertCount = this.getDeviceAlertCount(deviceId);
          return {
            id: deviceId,
            name: this.getDeviceName(deviceId),
            alertCount: alertCount,
          };
        });
    },
    getTotalAlertsForLocation(location) {
      const devices = this.getLocationDevices(location);
      return devices.reduce((total, device) => total + device.alertCount, 0);
    },
    getDeviceAlertCount(deviceId) {
      return this.alertDevices.filter((alert) => alert.id === deviceId).length;
    },
    getDeviceName(deviceId) {
      if (!deviceId) {
        return "Unknown Device";
      }

      const alertDevice = this.alertDevices.find((d) => d.id === deviceId);
      return alertDevice
        ? alertDevice.name
        : `Device ${deviceId.split("-").pop() || deviceId}`;
    },
    navigateToDevice(deviceId) {
      if (this.selectedLocation) {
        this.$router.push(
          `/location/${this.selectedLocation.key}/device/${deviceId}`
        );
      } else {
        const deviceLocation = this.items.find(
          (location) => location.devices && location.devices.includes(deviceId)
        );
        if (deviceLocation) {
          this.$router.push(
            `/location/${deviceLocation.key}/device/${deviceId}`
          );
        }
      }
    },
    async loadDevicesForLocations() {
      try {
        const allDevices = await fetchObjects("devices");

        if (allDevices && allDevices.length > 0) {
          const deviceMap = {};
          allDevices.forEach((device) => {
            if (device.key || device.deviceId) {
              const deviceKey = device.key || device.deviceId;
              deviceMap[deviceKey] = {
                id: deviceKey,
                name:
                  device.name ||
                  device.deviceId ||
                  `Device ${
                    deviceKey && deviceKey.split
                      ? deviceKey.split("-").pop()
                      : "Unknown"
                  }`,
                location: device.location,
                status: device.status || "Active",
              };
            }
          });

          this.deviceCache = deviceMap;

          this.alertDevices = this.alertDevices.map((alertDevice) => ({
            ...alertDevice,
            name:
              (deviceMap[alertDevice.id] && deviceMap[alertDevice.id].name) ||
              alertDevice.name,
            location:
              (deviceMap[alertDevice.id] &&
                deviceMap[alertDevice.id].location) ||
              alertDevice.location,
          }));
        }
      } catch (error) {
        console.error("Error loading devices for locations:", error);
      }
    },
    showLocationCard(location, event) {
      this.clickedLocation = location;
      this.clickCardPosition.x = event.clientX;
      this.clickCardPosition.y = event.clientY;
    },
    hideLocationCard() {
      this.clickedLocation = null;
    },
  },
  mounted() {
    this.loadUserLocations();
    this.loadDevicesForLocations();
  },
  async created() {
    await this.loadUserLocations();
  },
  watch: {
    "$store.state.auth.uid": function (newUid) {
      if (newUid) {
        this.$store.dispatch("handleUserLocations", newUid);
        this.fetchDeviceAlerts();
      }
    },
  },
};
</script>

<style>
@import "~leaflet/dist/leaflet.css";

/* Add animation for markers */
.leaflet-marker-icon {
  animation: dropIn 0.5s ease-out;
}

@keyframes dropIn {
  from {
    transform: translateY(-200px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Hide attribution control */
.leaflet-control-attribution {
  display: none !important;
}

/* Custom popup styling */
::v-deep .custom-popup .leaflet-popup-content-wrapper {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.location-popup {
  min-width: 300px;
  max-width: 400px;
}

.popup-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: #1976d2;
}

.popup-content {
  max-height: 300px;
  overflow-y: auto;
}

.devices-section h4 {
  font-size: 1rem;
  font-weight: 500;
  color: #424242;
  display: flex;
  align-items: center;
}

.devices-list {
  max-height: 200px;
  overflow-y: auto;
}

.device-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  margin: 4px 0;
  background: #f5f5f5;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.device-item:hover {
  background: #e3f2fd;
  transform: translateX(2px);
}

.device-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.device-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #424242;
}

.device-arrow {
  color: #757575;
}

.more-devices {
  text-align: center;
  padding: 8px;
  color: #757575;
  font-style: italic;
}

.no-devices {
  text-align: center;
  padding: 16px;
}

.popup-actions {
  margin-top: 8px;
}

/* Alert badge styling */
.alert-badge {
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
  font-weight: 500;
}

.alert-badge.error {
  background-color: #f44336;
  color: white;
}

.alert-badge.success {
  background-color: #4caf50;
  color: white;
}

/* View all button styling */
.view-all-btn {
  width: 100%;
  padding: 8px 16px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.view-all-btn:hover {
  background-color: #1565c0;
}

/* Divider styling */
hr.my-3 {
  margin: 12px 0;
  border: none;
  border-top: 1px solid #e0e0e0;
}

/* Custom alerts styling */
.custom-alerts-container {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1000;
  pointer-events: none;
  padding: 16px;
  max-width: 100%;
}

.custom-alert {
  background-color: #d32f2f; /* red darken-2 */
  color: white;
  border-radius: 4px;
  padding: 10px 16px;
  margin-top: 8px;
  width: 415px;
  box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2),
    0 6px 10px 0 rgba(0, 0, 0, 0.14), 0 1px 18px 0 rgba(0, 0, 0, 0.12);
  pointer-events: auto;
  cursor: pointer;
  position: fixed;
  right: 16px;
  display: flex;
  align-items: center;
  font-size: 13px;
  line-height: 1.4;
}

.custom-alert-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.alert-text {
  flex-grow: 1;
}

.close-btn {
  color: white !important;
}

/* Alert transition animations */
.alert-transition-enter-active,
.alert-transition-leave-active {
  transition: all 0.3s;
}

.alert-transition-enter,
.alert-transition-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .location-popup {
    min-width: 250px;
    max-width: 300px;
  }

  .custom-alert {
    width: calc(100vw - 32px);
    max-width: 400px;
  }
}

/* Click Card Styling */
.click-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 0;
  min-width: 280px;
  max-width: 320px;
  z-index: 1000 !important;
  animation: fadeInUp 0.2s ease-out;
  border: 1px solid rgba(0, 0, 0, 0.08);
  pointer-events: auto;
  position: absolute;
}

.click-card-content {
  padding: 16px;
}

.click-header h4 {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #1976d2;
}

.click-header {
  position: relative;
}

.click-header .close-btn {
  position: absolute;
  top: -4px;
  right: -4px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.click-header .close-btn:hover {
  background-color: #f5f5f5;
  color: #333;
}

.location-subtitle {
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 12px 0;
}

.click-stats {
  margin: 12px 0;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 6px 0;
  font-size: 0.875rem;
}

.stat-label {
  color: #424242;
  font-weight: 500;
}

.stat-value {
  color: #1976d2;
  font-weight: 600;
}

.stat-value.alert-count {
  color: #f44336;
}

.click-actions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.click-btn {
  width: 100%;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.click-btn.primary {
  background-color: #1976d2;
  color: white;
}

.click-btn.primary:hover {
  background-color: #1565c0;
  transform: translateY(-1px);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Map container positioning for click cards */
.leaflet-container {
  position: relative;
}
</style>
